import { ISelectOption } from '@constants/entities/ui';
import { IDomain } from '@store/services/projects/types';
import { findWideDomain } from '@utils/domains';
import { ITreeItem } from '@views/Project/helpers';

type CheckIsAllDomainsSelectedParams = {
  selectedDomains: ISelectOption[];
  projectDomains: IDomain[];
};

const findNode = (
  node: ITreeItem | undefined,
  nodeId: string,
): ITreeItem | null => {
  if (!node) return null;
  if (node.id === nodeId) return node;
  for (const child of node.children ?? []) {
    const found = findNode(child, nodeId);
    if (found) return found;
  }
  return null;
};

const collectOptions = (node: ITreeItem | undefined): ISelectOption[] => {
  if (!node) return [];
  return [
    { value: node.id, label: node.name },
    ...(node.children ?? []).flatMap((child) => collectOptions(child)),
  ];
};
export const getNodeWithDescendantsOptions = (
  root: ITreeItem | undefined,
  nodeId: string,
): ISelectOption[] => {
  const startNode = findNode(root, nodeId);
  return collectOptions(startNode ?? undefined);
};
export const getNodeDescendantIds = (
  root: ITreeItem | undefined,
  nodeId: string,
): string[] => {
  const options = getNodeWithDescendantsOptions(root, nodeId);
  return options.map((o) => o.value as string);
};

export const checkIsAllDomainsSelected = ({
  selectedDomains,
  projectDomains,
}: CheckIsAllDomainsSelectedParams) => {
  const wideDomain = findWideDomain(projectDomains);

  const isAllDomainsSelected = projectDomains.every(
    ({ id }) =>
      wideDomain?.id === id ||
      selectedDomains.some(({ value }) => value === id),
  );

  const isWideSelected = selectedDomains.some(
    ({ value }) => value === wideDomain?.id,
  );

  return { isAllDomainsSelected, isWideSelected };
};

export type GetSelectedDomainsParams = {
  selectedDomains: ISelectOption[] | undefined;
  wideDomain: ITreeItem | undefined;
};

export const getSelectedDomains = ({
  selectedDomains,
  wideDomain,
}: GetSelectedDomainsParams) => {
  const isWideSelected = selectedDomains?.some(
    ({ value }) => value === wideDomain?.id,
  );

  if (isWideSelected) {
    return (wideDomain?.children ?? []).flatMap((child) =>
      collectOptions(child).map(({ value }) => value as string),
    );
  }

  const currentValues =
    (selectedDomains?.map(({ value }) => value) as string[]) ?? [];

  return currentValues;
};
